import React, { useMemo, useState } from 'react';
import {
  format,
  startOfDay,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  addDays,
  addMonths,
  subMonths,
} from 'date-fns';
import { Box, Grid, Text, useStyleConfig, IconButton, SimpleGrid, useBreakpointValue } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { CheckBurst } from './CheckBurst';
import { StatCard } from './StatCard';

// Week starts on Sunday (locale default). 0 = Sunday per date-fns.
const WEEK_STARTS_ON = 0;

// Month-nav chevron hit area. 44px is the touch-target minimum; desktop
// pointers don't need it, so the button tightens up from md.
const NAV_BUTTON_SIZE = { base: '44px', md: '32px' };

// Pure helper: build a weekday-aligned month grid. Returns one cell per day from
// the start of the first week to the end of the last week, so the length is always
// a multiple of 7. Leading/trailing cells belong to the adjacent months.
export function buildMonthGrid(monthDate, { weekStartsOn = WEEK_STARTS_ON, today = new Date() } = {}) {
  const monthStart = startOfMonth(monthDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn });
  const gridEnd = endOfWeek(endOfMonth(monthDate), { weekStartsOn });

  return eachDayOfInterval({ start: gridStart, end: gridEnd }).map((date) => ({
    date,
    inCurrentMonth: isSameMonth(date, monthStart),
    isToday: isSameDay(date, today),
  }));
}

/*
 * Pure helper: summarise one calendar month from a habit's logs.
 *
 * Returns { daysChecked, bestRun, tracked }:
 *  - daysChecked: distinct calendar days logged in the month.
 *  - bestRun:     longest consecutive run *within* the month. A run spanning a
 *                 month boundary is clipped, so this describes the month rather
 *                 than duplicating the global streak.
 *  - tracked:     false when the month lies outside the habit's lifetime —
 *                 entirely before createdTs, or entirely in the future. The
 *                 caller shows an empty state instead of a misleading 0.
 *
 * The current month is scored only up to today; past months score in full.
 * Logs are deduped to one entry per calendar day so several logs on the same
 * day never inflate the count.
 */
export function computeMonthStats(habitLogs = [], monthDate, { today = new Date(), createdTs } = {}) {
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const todayStart = startOfDay(today);

  const startedAt = createdTs ? startOfDay(new Date(createdTs)) : null;
  const tracked = !(monthStart > todayStart) && !(startedAt && monthEnd < startedAt);

  // Scoring window ends at today for the in-progress month, at month end for
  // past months. For a future month this makes the range empty.
  const lastDay = monthEnd > todayStart ? todayStart : monthEnd;

  const loggedKeys = new Set();
  habitLogs.forEach((log) => {
    const date = startOfDay(new Date(log.recordTs));
    if (date >= monthStart && date <= lastDay) {
      loggedKeys.add(format(date, 'yyyy-MM-dd'));
    }
  });

  // Walking local Date objects keeps every comparison in local time — parsing
  // the yyyy-MM-dd keys back would resolve them as UTC and shift the day (and
  // so the month) in negative-offset timezones.
  let daysChecked = 0;
  let bestRun = 0;
  let run = 0;
  for (let d = monthStart; d <= lastDay; d = addDays(d, 1)) {
    if (loggedKeys.has(format(d, 'yyyy-MM-dd'))) {
      daysChecked += 1;
      run += 1;
      if (run > bestRun) bestRun = run;
    } else {
      run = 0;
    }
  }

  return { daysChecked, bestRun, tracked };
}

// Named dayCount rather than dayLabel: the cell renderer below already binds
// dayLabel to a per-cell accessible name.
const dayCount = (n) => `${n} ${n === 1 ? 'day' : 'days'}`;

// onToggleDay (optional): called with the cell's Date when the user taps an
// in-month day up to today. When omitted the calendar stays display-only.
// burstDay (optional): the Date whose cell plays the check-burst reward
// animation in place of its day number (the cell the user just checked).
// createdTs (optional): when the habit was created. Months that end before it
// show an empty summary rather than a "0 days" that reads like a failure.
export function Calendar({ habitLogs, onToggleDay, burstDay, createdTs }) {
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const sx = useStyleConfig('Calendar');

  // Single-letter weekday labels on narrow screens, three-letter from md up.
  const weekdayFormat = useBreakpointValue({ base: 'EEEEE', md: 'EEE' }) ?? 'EEE';

  // Converts log timestamps to Date objects once for comparison.
  const logDates = useMemo(() => habitLogs.map((log) => new Date(log.recordTs)), [habitLogs]);
  const isLogged = (date) => logDates.some((logDate => isSameDay(logDate, date)));

  const cells = useMemo(() => buildMonthGrid(currentMonth, { weekStartsOn: WEEK_STARTS_ON }), [currentMonth]);

  const stats = useMemo(
    () => computeMonthStats(habitLogs, currentMonth, { createdTs }),
    [habitLogs, currentMonth, createdTs]
  );

  // Weekday header derived from a reference week so the order honours WEEK_STARTS_ON.
  const weekdays = useMemo(() => {
    const refStart = startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_ON });
    return eachDayOfInterval({ start: refStart, end: endOfWeek(refStart, { weekStartsOn: WEEK_STARTS_ON }) });
  }, []);

  const handlePrevMonth = () => setCurrentMonth((m) => subMonths(m, 1));
  const handleNextMonth = () => setCurrentMonth((m) => addMonths(m, 1));

  const isCurrentMonth = startOfMonth(currentMonth) >= startOfMonth(new Date());

  return (
    <>
      {/* Cap the grid width and centre it so cells stay square on wide cards.
          The header and month summary share the same column so they line up
          with the grid's outer edges. */}
      <Box maxW="440px" mx="auto" w="100%">
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          {/* 44px square on touch-sized screens: the chevrons replaced two
              full-width buttons, and shrinking them to the icon must not take
              the tap target below the 44px minimum the day cells also honour. */}
          <IconButton
            aria-label="Previous month"
            icon={<ChevronLeftIcon boxSize={6} />}
            onClick={handlePrevMonth}
            variant="ghost"
            minW={NAV_BUTTON_SIZE}
            h={NAV_BUTTON_SIZE}
          />
          <Text fontSize="xl" fontWeight="bold" textAlign="center">
            {format(currentMonth, 'MMMM yyyy')}
          </Text>
          <IconButton
            aria-label="Next month"
            icon={<ChevronRightIcon boxSize={6} />}
            onClick={handleNextMonth}
            isDisabled={isCurrentMonth}
            variant="ghost"
            minW={NAV_BUTTON_SIZE}
            h={NAV_BUTTON_SIZE}
          />
        </Box>

        {stats.tracked ? (
          <SimpleGrid columns={2} spacing={2} mb={4} data-testid="month-stats">
            <StatCard testId="month-stat" label="Days checked" value={dayCount(stats.daysChecked)} />
            <StatCard testId="month-stat" label="Best run" value={dayCount(stats.bestRun)} />
          </SimpleGrid>
        ) : (
          // Same tile as the stat cards so the grid below doesn't jump when the
          // user pages across the habit's start date.
          <SimpleGrid columns={1} spacing={2} mb={4}>
            <StatCard testId="month-stats-empty" label="This habit didn't exist yet" value="—" />
          </SimpleGrid>
        )}
        <Grid templateColumns="repeat(7, 1fr)" gap={2} mb={2}>
          {weekdays.map((day) => (
            <Text
              key={format(day, 'EEEE')}
              data-testid="weekday-header"
              textAlign="center"
              fontSize="sm"
              fontWeight="semibold"
              color="gray.500"
            >
              {format(day, weekdayFormat)}
            </Text>
          ))}
        </Grid>

        <Grid templateColumns="repeat(7, 1fr)" gap={2} sx={sx}>
          {cells.map(({ date, inCurrentMonth, isToday }) => {
            const logged = inCurrentMonth && isLogged(date);
            // In-month days up to today are interactive; future days and
            // adjacent-month overflow cells (which can include "today" when
            // browsing an earlier month) stay display-only.
            const tappable = Boolean(onToggleDay) && inCurrentMonth && date <= new Date();
            const dayLabel = isToday ? 'today' : format(date, 'MMMM d');
            let bg = 'gray.100';
            if (!inCurrentMonth) bg = 'transparent';
            else if (logged) bg = 'green.200';

            return (
              <Box
                key={date.toISOString()}
                as={tappable ? 'button' : undefined}
                type={tappable ? 'button' : undefined}
                onClick={tappable ? () => onToggleDay(date) : undefined}
                aria-label={tappable ? (logged ? `Remove check-in for ${dayLabel}` : `Check in for ${dayLabel}`) : undefined}
                aria-pressed={tappable ? logged : undefined}
                cursor={tappable ? 'pointer' : undefined}
                _hover={tappable ? { bg: logged ? 'green.300' : 'gray.200' } : undefined}
                // Cells shrink to ~30px on narrow phones; extend each hit area
                // by half the 8px grid gap so neighbouring targets tile
                // edge-to-edge without overlapping or changing the layout.
                position={tappable ? 'relative' : undefined}
                _after={tappable ? {
                  content: '""',
                  position: 'absolute',
                  inset: '-4px',
                } : undefined}
                data-testid="calendar-cell"
                data-in-month={inCurrentMonth}
                data-today={isToday}
                data-logged={logged}
                display="flex"
                alignItems="center"
                justifyContent="center"
                // touchAction prevents the iOS double-tap-zoom delay on the tap target.
                sx={{ aspectRatio: '1', touchAction: tappable ? 'manipulation' : undefined }}
                bg={bg}
                color={inCurrentMonth ? 'inherit' : 'gray.400'}
                opacity={inCurrentMonth ? 1 : 0.55}
                border={isToday ? '2px solid' : undefined}
                borderColor={isToday ? 'blue.400' : undefined}
                borderRadius="md"
              >
                {logged && burstDay && isSameDay(date, burstDay) ? (
                  <CheckBurst burst />
                ) : (
                  <Text textAlign="center" fontWeight={isToday ? 'bold' : 'normal'}>
                    {format(date, 'd')}
                  </Text>
                )}
              </Box>
            );
          })}
        </Grid>
      </Box>
    </>
  );
}
